import type {
	AclEntryDto,
	AclEntryDtoRead,
	Permission,
} from '@/rtkApi/mddApiSliceGeneratedV2'

export const ACL_PERMISSIONS: Permission[] = [
	'VIEW',
	'EDIT',
	'MANAGE_ACCESS',
	'FORCE_UNLOCK',
]

/** Stable fallback so RTK `data ?? []` does not allocate a new `[]` every render (breaks useEffect deps). */
export const EMPTY_PARENT_EFFECTIVE_ACL: AclEntryDtoRead[] = []

export const permissionToLabelKey: Record<Permission, string> = {
	VIEW: 'PERMISSION_VIEW',
	EDIT: 'PERMISSION_EDIT',
	MANAGE_ACCESS: 'PERMISSION_MANAGE_ACCESS',
	FORCE_UNLOCK: 'PERMISSION_FORCE_UNLOCK',
}

export type PermissionChoice = 'inherit' | 'allow' | 'deny'

export type AclEditState =
	| { type: 'add' }
	| { entry: AclEntryDtoRead; type: 'edit' }
	| null

export const getInitialPermissionState = (): Record<
	Permission,
	PermissionChoice
> =>
	ACL_PERMISSIONS.reduce(
		(acc, p) => {
			acc[p] = 'inherit'
			return acc
		},
		{} as Record<Permission, PermissionChoice>,
	)

export const entryToPermissionState = (
	entry: Pick<AclEntryDtoRead, 'allowedPermissions' | 'deniedPermissions'>,
): Record<Permission, PermissionChoice> => {
	const state = getInitialPermissionState()
	const allowed = new Set(entry.allowedPermissions ?? [])
	const denied = new Set(entry.deniedPermissions ?? [])
	ACL_PERMISSIONS.forEach((p) => {
		if (denied.has(p)) state[p] = 'deny'
		else if (allowed.has(p)) state[p] = 'allow'
	})
	return state
}

export const permissionStateToPayload = (
	state: Record<Permission, PermissionChoice>,
): Pick<AclEntryDto, 'allowedPermissions' | 'deniedPermissions'> => ({
	allowedPermissions: ACL_PERMISSIONS.filter((p) => state[p] === 'allow'),
	deniedPermissions: ACL_PERMISSIONS.filter((p) => state[p] === 'deny'),
})

export const getEntryPermissionChoice = (
	entry: Pick<AclEntryDtoRead, 'allowedPermissions' | 'deniedPermissions'>,
	p: Permission,
): PermissionChoice => {
	const allowed = entry.allowedPermissions ?? []
	const denied = entry.deniedPermissions ?? []
	if (denied.includes(p)) return 'deny'
	if (allowed.includes(p)) return 'allow'
	return 'inherit'
}

export function getAclEntryCardKey(entry: AclEntryDtoRead, index: number): string {
	if (entry.id != null) return String(entry.id)
	if (entry.groupName) return `group:${entry.groupName}`
	return `idx:${index}`
}
